import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { messageAPI, notificationAPI } from '../services/api';
import { Home, CheckSquare, Target, Gift, DollarSign, User, LogOut, Share2, FileText, MessageSquare, Link2, Shield, Bell, CheckCheck, Trash2, X, Link } from 'lucide-react';
import './Layout.css';

function formatTimeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

const notificationIcons = {
  task_assigned: '📋',
  task_completed: '✅',
  content_approved: '👍',
  content_rejected: '❌',
  reward_shipped: '📦',
  reward_delivered: '🎁',
  payout_processed: '💰',
  tier_promotion: '🏆',
  broadcast: '📢',
  campaign_joined: '🎯',
  points_earned: '⭐',
  system: '🔔',
};

function Layout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch unread message count
  const { data: unreadData } = useQuery({
    queryKey: ['unread-count'],
    queryFn: () => messageAPI.getUnreadCount().then(res => res.data),
    refetchInterval: 30000, // Poll every 30 seconds
  });

  const unreadCount = unreadData?.unread_count || 0;

  // Fetch notification unread count (badge)
  const { data: notifCountData } = useQuery({
    queryKey: ['notification-unread-count'],
    queryFn: () => notificationAPI.getUnreadCount().then(res => res.data),
    refetchInterval: 15000, // Poll every 15 seconds
  });

  const notifUnread = notifCountData?.unread_count || 0;

  // Fetch notifications when dropdown is open
  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationAPI.getAll({ limit: 20 }).then(res => res.data),
    enabled: showNotifications,
    refetchInterval: showNotifications ? 10000 : false,
  });

  const notifications = notifData?.notifications || [];

  // Mark one as read
  const markReadMut = useMutation({
    mutationFn: (id) => notificationAPI.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  // Mark all as read
  const markAllReadMut = useMutation({
    mutationFn: () => notificationAPI.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  // Delete notification
  const deleteNotifMut = useMutation({
    mutationFn: (id) => notificationAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification-unread-count'] });
    },
  });

  const handleNotifClick = (notif) => {
    if (!notif.read) markReadMut.mutate(notif.id);
    if (notif.link) {
      setShowNotifications(false);
      navigate(notif.link);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>BA Platform</h1>
        </div>
        
        <nav className="sidebar-nav">
          <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
            <Home size={20} />
            <span>Dashboard</span>
          </NavLink>
          <NavLink to="/tasks" className={({ isActive }) => isActive ? 'active' : ''}>
            <CheckSquare size={20} />
            <span>Tasks</span>
          </NavLink>
          <NavLink to="/posts" className={({ isActive }) => isActive ? 'active' : ''}>
            <FileText size={20} />
            <span>Updates</span>
          </NavLink>
          <NavLink to="/messages" className={({ isActive }) => isActive ? 'active' : ''}>
            <MessageSquare size={20} />
            <span>Messages</span>
            {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
          </NavLink>
          <NavLink to="/campaigns" className={({ isActive }) => isActive ? 'active' : ''}>
            <Target size={20} />
            <span>Campaigns</span>
          </NavLink>
          <NavLink to="/rewards" className={({ isActive }) => isActive ? 'active' : ''}>
            <Gift size={20} />
            <span>Rewards</span>
          </NavLink>
          <NavLink to="/earnings" className={({ isActive }) => isActive ? 'active' : ''}>
            <DollarSign size={20} />
            <span>Earnings</span>
          </NavLink>
          <NavLink to="/social" className={({ isActive }) => isActive ? 'active' : ''}>
            <Share2 size={20} />
            <span>Social</span>
          </NavLink>
          <NavLink to="/track-post" className={({ isActive }) => isActive ? 'active' : ''}>
            <Link2 size={20} />
            <span>Track Post</span>
          </NavLink>
          <NavLink to="/referral-links" className={({ isActive }) => isActive ? 'active' : ''}>
            <Link size={20} />
            <span>Referral Links</span>
          </NavLink>
          <NavLink to="/profile" className={({ isActive }) => isActive ? 'active' : ''}>
            <User size={20} />
            <span>Profile</span>
          </NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin" className={({ isActive }) => isActive ? 'active' : ''}>
              <Shield size={20} />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      
      <main className="main-content">
        <header className="top-bar">
          <div className="top-bar-actions">
            {/* Notification Bell */}
            <div className="notification-wrapper" ref={notifRef}>
              <button
                className="notification-bell"
                onClick={() => setShowNotifications(!showNotifications)}
                title="Notifications"
              >
                <Bell size={20} />
                {notifUnread > 0 && (
                  <span className="notification-badge">{notifUnread > 99 ? '99+' : notifUnread}</span>
                )}
              </button>

              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notif-header">
                    <h3>Notifications</h3>
                    <div className="notif-header-actions">
                      {notifUnread > 0 && (
                        <button
                          className="notif-mark-all"
                          onClick={() => markAllReadMut.mutate()}
                          title="Mark all as read"
                        >
                          <CheckCheck size={16} />
                          <span>Mark all read</span>
                        </button>
                      )}
                      <button className="notif-close" onClick={() => setShowNotifications(false)}>
                        <X size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="notif-list">
                    {notifications.length === 0 ? (
                      <div className="notif-empty">
                        <Bell size={32} color="#9ca3af" />
                        <p>No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          className={`notif-item ${!notif.read ? 'notif-unread' : ''}`}
                          onClick={() => handleNotifClick(notif)}
                        >
                          <div className="notif-icon">{notificationIcons[notif.type] || '🔔'}</div>
                          <div className="notif-content">
                            <div className="notif-title">{notif.title}</div>
                            {notif.message && <div className="notif-message">{notif.message}</div>}
                            <div className="notif-time">{formatTimeAgo(notif.created_at)}</div>
                          </div>
                          <button
                            className="notif-delete"
                            onClick={(e) => { e.stopPropagation(); deleteNotifMut.mutate(notif.id); }}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="user-info">
            <div className="user-avatar">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div>
              <div className="user-name">{user?.first_name} {user?.last_name}</div>
              <div className="user-tier">{user?.tier || 'Bronze'} Tier</div>
            </div>
          </div>
        </header>
        
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default Layout;
